/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { object, string } from 'prop-types';
import { triggerPassportSubscription, clearPassportError } from '../../../../../state/ducks/Passport/Passport-Action';
import { getPassportData, getPassportItem, getPassportError, getPassportLoader, getPassportMemberProfile, getPassportABData, getPassportCampaignData, getPassportSignInData } from '../../../../../state/ducks/Passport/Passport-Selectors';
import { getIsPassportUserLoggedIn } from '../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getActiveABTests } from '../../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import validatePassport from '../../../../helpers/validatePassport';
import getTrackingLabel from '../../../../helpers/passportHelper';
import resolvePassportCopy from './resolvePassportCopy';
import selectPassportOffer from './selectPassportOffer';

const useStyles = makeStyles(() => ({
    button: {
        display: 'flex',
        width: 630,
        maxWidth: 'calc(100vw - 48px)',
        minHeight: 74,
        marginLeft: '50%',
        transform: 'translateX(-50%)',
        borderRadius: 0,
        background: '#fff',
        color: '#329A77',
        boxShadow: '0 2px 3px rgba(0, 0, 0, .15)',
        padding: '12px 20px',
        fontFamily: 'Fields, Georgia, serif',
        fontSize: 24,
        fontWeight: 700,
        '&:hover': { filter: 'brightness(.96)' },
        '@media (max-width: 760px)': { minHeight: 56, fontSize: 16 },
    },
    error: { margin: '14px auto 0', fontSize: 14 },
    inCart: { margin: '14px auto 0', fontSize: 14 },
    secondary: { display: 'block', margin: '16px auto 0', padding: 0, border: 0, background: 'transparent', color: 'inherit', fontSize: 16, textDecoration: 'underline', cursor: 'pointer' },
}));

const PassportLanderCta = ({ skus, cta, cartIconLabel, styling }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const passportData = useSelector(getPassportData);
    const passportItems = useSelector(getPassportItem);
    const passportError = useSelector(getPassportError);
    const passportLoader = useSelector(getPassportLoader);
    const profile = useSelector(getPassportMemberProfile);
    const isPassportUser = useSelector(getIsPassportUserLoggedIn);
    const abData = useSelector(getPassportABData);
    const campaignData = useSelector(getPassportCampaignData);
    const signInData = useSelector(getPassportSignInData);
    const flags = useSelector(getFeatureFlags);
    const activeABTests = useSelector(getActiveABTests);
    const [loading, setLoading] = useState(false);
    const active = validatePassport('isActivePassportUser', passportData);
    const isMember = Boolean((active || profile?.membershipType === 'P') && isPassportUser);
    const { sku, price, campaignApplied } = selectPassportOffer({ passportData, abData, signInData, campaignData, flags, activeABTests });
    const brandReady = Boolean(passportData?.brand_id && sku);
    const trackingLabel = getTrackingLabel({}, passportData, flags['is-passport-conditional-offer-on']);
    const template = campaignApplied ? (cta?.sale_cta_copy || cta?.standard_cta_copy) : cta?.standard_cta_copy;
    const buttonCopy = resolvePassportCopy(template || 'Join now', skus, { defaultIntroPrice: price ?? '', salePrice: price ?? '' });
    const hasPassportInCart = passportItems.length > 0;
    const buttonStyle = { backgroundColor: styling?.cta_background_color || '#fff', color: styling?.cta_font_color || '#329A77' };

    useEffect(() => { dispatch(clearPassportError()); }, [dispatch]);

    const join = () => {
        if (!brandReady) return;
        setLoading(true);
        dispatch(trackEvent({ eventCategory: 'Passport Click', eventAction: 'Passport Enrollment', eventLabel: trackingLabel }));
        dispatch(triggerPassportSubscription('passportPage', sku));
    };
    const checkout = () => {
        const id = passportItems?.[0]?.ordersId;
        if (id) history.push(`/checkout/cart/${id}`);
    };
    const continueShopping = () => {
        setLoading(false);
        dispatch(trackEvent({ eventCategory: 'Test Impression', eventAction: 'Continue Shopping', eventLabel: trackingLabel }));
        history.push('/');
    };

    if (hasPassportInCart) {
        return (
            <>
                <Button className={classes.button} variant="contained" onClick={checkout} style={buttonStyle}>Go To Checkout</Button>
                <button className={classes.secondary} type="button" onClick={continueShopping}>Continue Shopping</button>
                <p className={classes.inCart}>Passport is in your {cartIconLabel.toLowerCase()}. Complete checkout to activate your benefits.</p>
            </>
        );
    }
    if (isMember) {
        return <Button className={classes.button} variant="contained" onClick={continueShopping} style={buttonStyle}>Continue Shopping</Button>;
    }
    return (
        <>
            <Button className={classes.button} variant="contained" onClick={join} disabled={!brandReady || loading || passportLoader || Boolean(passportError?.length)} style={buttonStyle}>
                {(loading || passportLoader) && !passportError?.length ? <CircularProgress size={18} color="inherit" /> : buttonCopy}
            </Button>
            {passportError && <p className={classes.error} role="alert">{passportError}</p>}
        </>
    );
};

PassportLanderCta.propTypes = { skus: object, cta: object, cartIconLabel: string, styling: object };
PassportLanderCta.defaultProps = { skus: {}, cta: {}, cartIconLabel: 'Cart', styling: {} };
export default PassportLanderCta;
